class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.currentIndex = 0;
    this.touchStartY = 0;
    this.touchEndY = 0;
    this.announcementBar = this.querySelector("#wt-announcement__container");
    this.announcementInterval = null;
    this.countdownIntervals = [];
    this.isCarousel = !!this.announcementBar?.classList.contains(
      "wt-announcement__container--carousel",
    );
    this.speed = Number(this.announcementBar?.dataset.carouselSpeed) || 5000;
    this.reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    this.changeAnnouncement = this.changeAnnouncement.bind(this);
    this.previousAnnouncement = this.previousAnnouncement.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.hideForOneDay = this.hideForOneDay.bind(this);
    this.startInterval = this.startInterval.bind(this);
    this.stopInterval = this.stopInterval.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.initCountdowns = this.initCountdowns.bind(this);
  }

  connectedCallback() {
    if (this.isCarousel) this.initCarousel();

    this.closeButton = this.querySelector(".wt-announcement__close");
    if (this.closeButton)
      this.closeButton.addEventListener("click", this.hideForOneDay);

    // Initialize countdown timers (after cloning, so clones tick too)
    this.initCountdowns();
  }

  initCarousel() {
    const slides = Array.from(this.announcementBar.children);
    this.slideCount = slides.length;
    if (this.slideCount < 2) return;

    // Clone the first slide to the end for a seamless upward loop
    const clone = slides[0].cloneNode(true);
    clone.setAttribute("aria-hidden", "true");
    clone.removeAttribute("data-shopify-editor-block");
    clone
      .querySelectorAll("a, button")
      .forEach((el) => el.setAttribute("tabindex", "-1"));
    this.announcementBar.appendChild(clone);

    this.announcementBar.style.transition = this.reduceMotion
      ? "none"
      : "transform 0.5s ease-in-out";

    this.startInterval();

    this.addEventListener("mouseenter", this.stopInterval);
    this.addEventListener("mouseleave", this.startInterval);
    this.addEventListener("focusin", this.stopInterval);
    this.addEventListener("focusout", this.startInterval);

    this.announcementBar.addEventListener("touchstart", this.handleTouchStart, {
      passive: true,
    });
    this.announcementBar.addEventListener("touchmove", this.handleTouchMove, {
      passive: true,
    });
    this.announcementBar.addEventListener("touchend", this.handleTouchEnd, {
      passive: true,
    });

    // Theme editor: jump to the selected block
    this.handleBlockSelect = (e) => {
      const index = slides.indexOf(e.target);
      if (index === -1) return;
      this.stopInterval();
      this.goTo(index);
    };
    document.addEventListener("shopify:block:select", this.handleBlockSelect);
    document.addEventListener("shopify:block:deselect", this.startInterval);

    window.addEventListener("resize", this.handleResize);
  }

  startInterval() {
    if (!this.isCarousel || this.slideCount < 2) return;
    clearInterval(this.announcementInterval);
    this.announcementInterval = setInterval(this.changeAnnouncement, this.speed);
  }

  stopInterval() {
    clearInterval(this.announcementInterval);
  }

  goTo(index, animate = true) {
    if (!this.announcementBar) return;
    this.currentIndex = index;
    this.announcementBar.style.transition =
      animate && !this.reduceMotion ? "transform 0.5s ease-in-out" : "none";
    // Each slide is exactly 100% of the viewport height
    this.announcementBar.style.transform = `translateY(-${index * 100}%)`;
    this.announcementBar.style.setProperty("--index", index);
  }

  initCountdowns() {
    const countdowns = this.querySelectorAll(".wt-countdown");
    countdowns.forEach((countdown) => {
      const endDateStr = countdown.dataset.countdownEnd;
      if (!endDateStr) return;

      const updateCountdown = () => {
        const endDate = new Date(endDateStr);
        const now = new Date();
        const diff = endDate - now;

        const daysEl = countdown.querySelector("[data-countdown-days]");
        const hoursEl = countdown.querySelector("[data-countdown-hours]");
        const minutesEl = countdown.querySelector("[data-countdown-minutes]");
        const secondsEl = countdown.querySelector("[data-countdown-seconds]");

        if (diff <= 0) {
          // Countdown has expired
          const hideWhenExpired = countdown.dataset.hideWhenExpired === "true";
          const expiredText = countdown.dataset.expiredText;
          const countdownBlock = countdown.closest(".wt-announcement__countdown");

          if (hideWhenExpired && countdownBlock) {
            countdownBlock.style.display = "none";
          } else if (expiredText && countdownBlock) {
            countdown.innerHTML = `<span class="wt-countdown__expired">${expiredText}</span>`;
          } else {
            if (daysEl) daysEl.textContent = "00";
            if (hoursEl) hoursEl.textContent = "00";
            if (minutesEl) minutesEl.textContent = "00";
            if (secondsEl) secondsEl.textContent = "00";
          }
          return false; // Stop the interval
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);

        if (daysEl) daysEl.textContent = String(days).padStart(2, "0");
        if (hoursEl) hoursEl.textContent = String(hours).padStart(2, "0");
        if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, "0");
        if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, "0");

        return true; // Continue the interval
      };

      // Initial update
      updateCountdown();

      // Update every second
      const intervalId = setInterval(() => {
        const shouldContinue = updateCountdown();
        if (!shouldContinue) {
          clearInterval(intervalId);
        }
      }, 1000);

      this.countdownIntervals.push(intervalId);
    });
  }

  handleResize() {
    this.goTo(this.currentIndex % this.slideCount, false);
  }

  hideForOneDay() {
    const oneDayLater = new Date().getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem("wt-announcement-hidden", oneDayLater);
    this.style.display = "none";
  }

  changeAnnouncement() {
    this.goTo(this.currentIndex + 1);

    // Landed on the clone of the first slide: jump back without animation
    if (this.currentIndex === this.slideCount) {
      setTimeout(
        () => {
          this.goTo(0, false);
        },
        this.reduceMotion ? 0 : 500,
      );
    }
  }

  previousAnnouncement() {
    if (this.currentIndex === 0) {
      // Jump to the clone (visually identical) and animate up to the last slide
      this.goTo(this.slideCount, false);
      void this.announcementBar.offsetHeight;
    }
    this.goTo(this.currentIndex - 1);
  }

  handleTouchStart(e) {
    this.touchStartY = e.touches[0].clientY;
    this.touchEndY = this.touchStartY;
    this.stopInterval();
  }

  handleTouchMove(e) {
    this.touchEndY = e.touches[0].clientY;
  }

  handleTouchEnd() {
    const swipeThreshold = 20;
    const delta = this.touchEndY - this.touchStartY;

    if (Math.abs(delta) > swipeThreshold) {
      if (delta < 0) {
        this.changeAnnouncement();
      } else {
        this.previousAnnouncement();
      }
    }
    this.startInterval();
  }

  disconnectedCallback() {
    clearInterval(this.announcementInterval);
    this.countdownIntervals.forEach((intervalId) => clearInterval(intervalId));
    this.countdownIntervals = [];
    this.announcementBar?.removeEventListener(
      "touchstart",
      this.handleTouchStart,
    );
    this.announcementBar?.removeEventListener("touchmove", this.handleTouchMove);
    this.announcementBar?.removeEventListener("touchend", this.handleTouchEnd);
    window.removeEventListener("resize", this.handleResize);
    if (this.handleBlockSelect) {
      document.removeEventListener("shopify:block:select", this.handleBlockSelect);
      document.removeEventListener("shopify:block:deselect", this.startInterval);
    }
  }
}

customElements.define("announcement-bar", AnnouncementBar);
